import { Group, Line, Rect, Transformer } from 'react-konva'
import { Html } from 'react-konva-utils'
import {
  CutTag,
  EditTableFn,
  ExtractionMethod,
  Rectangle,
  TextBlock,
  TextcutRectangle,
  UserGeneratedLine,
} from '../../types'
import { getWordsInsideRect, isVertical } from '../../utils/spatial'
import 'react-toastify/dist/ReactToastify.css'
import useFormModal from '../FormModal/useFormModal'
import TableOptionsModalContent from '../TableOptions/TableOptionsModalContent'
import useTableOptionsProcess from '../TableOptions/useTableOptionsProcess'
import {
  CustomWord,
  TableOptionsModalResponseData,
} from '../TableOptions/types'
import TableDropdown from '../CustomDropdown/TableDropdown'
import RemoveLineIconButton from './RemoveLineIconButton'
import { useAppDispatch } from '../../dispatch'
import { KonvaEventObject } from 'konva/lib/Node'
import { Dispatch, useEffect, useRef, useState } from 'react'
import { Transformer as TransformerType } from 'konva/lib/shapes/Transformer'
import { Rect as RectType } from 'konva/lib/shapes/Rect'

type Props = {
  bindingId: string
  x: number
  y: number
  w: number
  h: number
  fill: string
  stroke: string
  ruling: null | JSX.Element
  changeRulingDirection: (bindingId: string, value: 0 | 1 | 2) => void
  rulingDirection: 0 | 1 | 2
  lines?: UserGeneratedLine[]
  removeLine: (id: string, lineId: string) => void
  tag: CutTag
  textCutRect: TextcutRectangle
  editTable: EditTableFn
  wordsBlocks: TextBlock[]
  extractionMethod: ExtractionMethod | undefined
  dispatch: ReturnType<typeof useAppDispatch>
  onMouseMove: (e: KonvaEventObject<MouseEvent>) => void
  onResizeEnd: (bindingId: string, rect: Rectangle, cb?: () => void) => void
  selectedBindingId: string
  setSelectedBindingId: Dispatch<React.SetStateAction<string>>
  onRectClick: (bindingId: string) => void
}

const getTransformerStyle = (tag: CutTag) => {
  switch (tag) {
    case CutTag.TEXTCUT:
      return {
        anchorStroke: 'rgb(182,251,251)',
        borderStroke: 'rgb(182,251,251)',
      }
    case CutTag.TABLE:
      return {
        anchorStroke: 'green',
        borderStroke: 'green',
      }
    case CutTag.SUM:
      return {
        anchorStroke: 'rgb(253,230,182)',
        borderStroke: 'rgb(253,230,182)',
      }
    case CutTag.EXCEPTION:
      return {
        anchorStroke: 'rgb(251, 208, 215)',
        borderStroke: 'rgb(251, 208, 215)',
      }
    case CutTag.VALIDATE:
      return {
        anchorStroke: 'rgb(204, 235, 224)',
        borderStroke: 'rgb(204, 235, 224)',
      }
    default:
      return {
        anchorStroke: '',
        borderStroke: '',
      }
  }
}

const RectGroup = ({
  h,
  w,
  x,
  y,
  fill,
  stroke,
  bindingId,
  ruling,
  changeRulingDirection,
  rulingDirection,
  lines,
  removeLine,
  tag,
  textCutRect,
  editTable,
  wordsBlocks,
  extractionMethod,
  dispatch,
  onMouseMove,
  onResizeEnd,
  selectedBindingId,
  setSelectedBindingId,
  onRectClick,
}: Props) => {
  const { showFormModal } = useFormModal()
  const { updateTextcutRect } = useTableOptionsProcess()
  const transformerRef = useRef<TransformerType>(null)
  const rectRef = useRef<RectType>(null)
  const [transforming, setTransforming] = useState(false)
  const [showBorder, setShowBorder] = useState(true)

  useEffect(() => {
    if (
      transformerRef.current &&
      rectRef.current &&
      selectedBindingId === bindingId
    ) {
      transformerRef.current.nodes([rectRef.current])
      transformerRef.current.getLayer()?.batchDraw()
    }
  }, [bindingId, selectedBindingId])

  const handleTableOptionsClick = async () => {
    const rect = {
      x: textCutRect.x,
      y: textCutRect.y,
      w: textCutRect.w,
      h: textCutRect.h,
    }

    const words: CustomWord[] = getWordsInsideRect(rect, wordsBlocks).map(
      (block) => {
        const x1 = block.boundingPoly.vertices[0].x
        const y1 = block.boundingPoly.vertices[0].y
        const x2 = block.boundingPoly.vertices[2].x
        const y2 = block.boundingPoly.vertices[2].y

        const word: CustomWord = {
          id: block.id,
          text: block.description,
          coords: [x1, y1, x2, y2],
        }

        return word
      }
    )

    const res = await showFormModal<TableOptionsModalResponseData>({
      title: 'Advanced table formatting',
      content: (
        <TableOptionsModalContent
          textCutRect={textCutRect}
        ></TableOptionsModalContent>
      ),
      submitText: 'Apply',
    })

    if (res.success && res.data) {
      const newTextcutRect = updateTextcutRect(
        textCutRect,
        res.data,
        words,
        extractionMethod
      )
      editTable(newTextcutRect, newTextcutRect.lines!, true)
    }
  }

  const onTransformStart = () => {
    setTransforming(true)
    setShowBorder(false)
  }

  return (
    <Group onMouseMove={onMouseMove}>
      <Rect
        ref={rectRef}
        x={x}
        y={y}
        width={w}
        height={h}
        fill={fill}
        stroke={showBorder ? stroke : ''}
        strokeWidth={2}
        name={bindingId}
        onClick={(e) => {
          e.cancelBubble = true
          setSelectedBindingId(bindingId)
          onRectClick(bindingId)
        }}
        onMouseDown={(e) => {
          // Prevent stage from getting the event
          e.cancelBubble = true
        }}
        onMouseMove={(e) => {
          if (rulingDirection === 0) e.cancelBubble = true
        }}
        onTransformStart={onTransformStart}
        onTransformEnd={() => {
          if (!rectRef.current) {
            setTransforming(false)
            setShowBorder(true)
            return
          }
          const node = rectRef.current
          const scaleX = node.scaleX()
          const scaleY = node.scaleY()
          const rect = {
            x: node.x(),
            y: node.y(),
            w: (node.width()) * scaleX,
            h: (node.height()) * scaleY,
          }
          setSelectedBindingId('')
          onResizeEnd(bindingId, rect, () => {
            setTransforming(false)
            setShowBorder(true)
          })
        }}
      />
      {tag !== CutTag.DATA_MATCH && bindingId === selectedBindingId && (
        <Transformer
          ref={transformerRef}
          flipEnabled={false}
          boundBoxFunc={(oldBox, newBox) => {
            if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
              return oldBox
            }
            return newBox
          }}
          enabledAnchors={[
            'middle-left',
            'middle-right',
            'top-center',
            'bottom-center',
            'top-left',
            'top-right',
            'bottom-left',
            'bottom-right',
          ]}
          rotateEnabled={false}
          onMouseDown={(e) => {
            // Prevent stage from getting the event
            e.cancelBubble = true
          }}
          keepRatio={false}
          {...getTransformerStyle(tag)}
        />
      )}
      {tag === CutTag.TABLE && (
        <>
          <Html
            transformFunc={(props) => {
              return {
                ...props,
                x: x + w + 2,
                y,
              }
            }}
          >
            <TableDropdown
              bindingId={bindingId}
              changeRulingDirection={changeRulingDirection}
              dispatch={dispatch}
              handleTableOptionsClick={handleTableOptionsClick}
              rulingDirection={rulingDirection}
            />
            {/* <button
              className={`bg-gray-100/50`}
              onClick={() => changeRulingDirection(bindingId)}
            >
              {rulingDirection ? (
                <MoreHorizOutlinedIcon sx={{ fontSize: 16 }} />
              ) : (
                <MoreVertOutlinedIcon sx={{ fontSize: 16 }} />
              )}
            </button> */}
          </Html>
          {/* <Html
            transformFunc={(props) => {
              return {
                ...props,
                x: x + w + 2,
                y: y + 24,
              }
            }}
          >
            <button
              className={`bg-gray-100/50`}
              onClick={handleTableOptionsClick}
            >
              <TuneOutlinedIcon sx={{ fontSize: 16 }} />
            </button>
          </Html> */}
          {rulingDirection !== 0 && ruling}
          {lines &&
            !transforming &&
            lines.length > 0 &&
            lines.map((line) => (
              <Group key={line.id}>
                <Line points={line.line} stroke={'green'} strokeWidth={2} />
                <Html
                  divProps={{ style: { width: 16, height: 16, zIndex: 1 } }}
                  transformFunc={(props) => {
                    const verticalLine = isVertical(line.line)
                    return {
                      ...props,
                      x: line.line.at(0)! + (verticalLine ? -7 : -9),
                      y: line.line.at(3)! - (verticalLine ? 12 : 14),
                    }
                  }}
                >
                  {rulingDirection === 0 && <></>}
                  {rulingDirection === 1 && !isVertical(line.line) && (
                    <RemoveLineIconButton
                      bindingId={bindingId}
                      lineId={line.id}
                      removeLine={removeLine}
                    />
                  )}
                  {rulingDirection === 2 && isVertical(line.line) && (
                    <RemoveLineIconButton
                      bindingId={bindingId}
                      lineId={line.id}
                      removeLine={removeLine}
                    />
                  )}
                </Html>
              </Group>
            ))}
        </>
      )}
    </Group>
  )
}

export default RectGroup
